import React from 'react';
import { MapPin, Clock, Users, Car, Phone, Mail, MessageSquare, CheckCircle, AlertCircle, Loader, X, CalendarDays } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useBookingForm from '@/hooks/useBookingForm';
import useIsMobile from '@/hooks/useIsMobile';

export const inputClass = "w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-[#555] focus:outline-none focus:border-[#EAB308]/50 focus:ring-1 focus:ring-[#EAB308]/20 transition-colors text-sm";
export const inputErrorClass = "w-full bg-[#111] border border-red-500/50 rounded-lg px-4 py-3 text-white placeholder-[#555] focus:outline-none focus:border-red-400/50 transition-colors text-sm";
export const labelClass = "block text-sm font-medium text-[#A3A3A3] mb-1.5";
export const errorClass = "text-red-400 text-xs mt-1 flex items-center gap-1";

export const FieldIcon = ({ icon: Icon }) => (
  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none">
    <Icon size={15} />
  </div>
);

const SuccessMessage = () => {
  const { t } = useTranslation();
  return (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="booking-success-msg"
    className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-10 text-center">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#EAB308]/10 border border-[#EAB308]/20 mb-6">
      <CheckCircle size={32} className="text-[#EAB308]" />
    </div>
    <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('booking.success_title')}</h3>
    <p className="text-[#A3A3A3] mb-2">{t('booking.success_msg')}</p>
    <p className="text-[#A3A3A3] mb-6">
      {t('booking.success_contact')} <span className="text-white font-medium">{t('booking.success_delay')}</span> {t('booking.success_confirm')}
    </p>
    <p className="text-[#A3A3A3] text-sm">
      {t('booking.success_urgent')}{' '}
      <a href="tel:+33674626368" className="text-[#EAB308] hover:underline font-medium">{t('booking.success_call')}</a>
    </p>
  </motion.div>
  );
};

const DateField = ({ value, min, onChange, onClear, error, label, placeholder }) => {
  const isMobile = useIsMobile();
  const displayDate = value
    ? new Date(value + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  if (isMobile) {
    return (
      <div>
        <label className={labelClass}>{label}</label>
        <div className="relative">
          <div className={`${error ? inputErrorClass : inputClass} flex items-center justify-between select-none pointer-events-none`}>
            <span className={`text-sm ${value ? 'text-white' : 'text-[#555]'}`}>{displayDate || placeholder}</span>
            <CalendarDays size={15} className="text-[#555] shrink-0 ml-2" />
          </div>
          <input
            type="date" name="date" value={value} min={min} onChange={onChange}
            data-testid="booking-date"
            className="absolute inset-0 opacity-0 w-full cursor-pointer z-10"
            style={{ fontSize: 16 }}
          />
          {value && (
            <button type="button" onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 text-[#555] hover:text-white p-1" aria-label="Effacer la date">
              <X size={13} />
            </button>
          )}
        </div>
        {error && <p className={errorClass}><AlertCircle size={12} />{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <input
          type="date" name="date" value={value} min={min} onChange={onChange}
          data-testid="booking-date"
          className={`${error ? inputErrorClass : inputClass} min-w-0`}
          style={{ colorScheme: 'dark', maxWidth: '100%' }}
        />
        {value && (
          <button type="button" onClick={onClear} className="absolute right-10 top-1/2 -translate-y-1/2 text-[#555] hover:text-white z-10 p-1" aria-label="Effacer la date">
            <X size={13} />
          </button>
        )}
      </div>
      {error && <p className={errorClass}><AlertCircle size={12} />{error}</p>}
    </div>
  );
};

const TimeField = ({ value, onChange, onClear, error, label }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div>
        <label className={labelClass}>{label}</label>
        <div className="relative">
          <div className={`${error ? inputErrorClass : inputClass} flex items-center select-none pointer-events-none`}>
            <Clock size={15} className="text-[#555] shrink-0 mr-3" />
            <span className={`text-sm ${value ? 'text-white' : 'text-[#555]'}`}>{value || '--:--'}</span>
          </div>
          <input
            type="time" name="time" value={value} onChange={onChange}
            data-testid="booking-time"
            className="absolute inset-0 opacity-0 w-full cursor-pointer z-10"
            style={{ fontSize: 16 }}
          />
          {value && (
            <button type="button" onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 text-[#555] hover:text-white p-1" aria-label="Effacer l'heure">
              <X size={13} />
            </button>
          )}
        </div>
        {error && <p className={errorClass}><AlertCircle size={12} />{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <FieldIcon icon={Clock} />
        <input
          type="time" name="time" value={value} onChange={onChange}
          data-testid="booking-time"
          className={`${error ? inputErrorClass : inputClass} pl-10 min-w-0`}
          style={{ colorScheme: 'dark', maxWidth: '100%', lineHeight: '1.5' }}
        />
        {value && (
          <button type="button" onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white z-10 p-1" aria-label="Effacer l'heure">
            <X size={13} />
          </button>
        )}
      </div>
      {error && <p className={errorClass}><AlertCircle size={12} />{error}</p>}
    </div>
  );
};

const BookingForm = () => {
  const today = new Date().toISOString().split('T')[0];
  const { t } = useTranslation();
  const { form, errors, loading, submitted, apiError, handleChange, clearField, handleSubmit } = useBookingForm(t);

  if (submitted) return <SuccessMessage />;

  return (
    <form onSubmit={handleSubmit} data-testid="booking-form" className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 sm:p-8" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        <div>
          <label className={labelClass}>{t('booking.departure')}</label>
          <div className="relative">
            <FieldIcon icon={MapPin} />
            <input type="text" name="departure" value={form.departure} onChange={handleChange} placeholder={t('booking.departure_placeholder')} data-testid="booking-departure" className={`${errors.departure ? inputErrorClass : inputClass} pl-10`} />
          </div>
          {errors.departure && <p className={errorClass}><AlertCircle size={12} />{errors.departure}</p>}
        </div>

        <div>
          <label className={labelClass}>{t('booking.destination')}</label>
          <div className="relative">
            <FieldIcon icon={MapPin} />
            <input type="text" name="destination" value={form.destination} onChange={handleChange} placeholder={t('booking.destination_placeholder')} data-testid="booking-destination" className={`${errors.destination ? inputErrorClass : inputClass} pl-10`} />
          </div>
          {errors.destination && <p className={errorClass}><AlertCircle size={12} />{errors.destination}</p>}
        </div>

        <DateField value={form.date} min={today} onChange={handleChange} onClear={() => clearField('date')} error={errors.date} label={t('booking.date')} placeholder={t('booking.date_placeholder')} />
        <TimeField value={form.time} onChange={handleChange} onClear={() => clearField('time')} error={errors.time} label={t('booking.time')} />

        <div>
          <label className={labelClass}>{t('booking.passengers')}</label>
          <div className="relative">
            <FieldIcon icon={Users} />
            <select name="passengers" value={form.passengers} onChange={handleChange} data-testid="booking-passengers" className={`${inputClass} pl-10 appearance-none cursor-pointer`}>
              {[1,2,3,4,5,6,7].map(n => (
                <option key={n} value={n} className="bg-[#1A1A1A] text-white">{n} {t('fleet.passengers')}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>{t('booking.vehicle')}</label>
          <div className="relative">
            <FieldIcon icon={Car} />
            <select name="vehicle_type" value={form.vehicle_type} onChange={handleChange} data-testid="booking-vehicle-type" className={`${inputClass} pl-10 appearance-none cursor-pointer`}>
              <option value="" className="bg-[#1A1A1A] text-[#555]">{t('booking.vehicle_none')}</option>
              <option value="mercedes_van" className="bg-[#1A1A1A] text-white">Mercedes Classe V (7 places)</option>
              <option value="mercedes_eqb" className="bg-[#1A1A1A] text-white">Mercedes EQB (5 places)</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>{t('booking.phone')}</label>
          <div className="relative">
            <FieldIcon icon={Phone} />
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder={t('booking.phone_placeholder')} data-testid="booking-phone" className={`${errors.phone ? inputErrorClass : inputClass} pl-10`} />
          </div>
          {errors.phone && <p className={errorClass}><AlertCircle size={12} />{errors.phone}</p>}
        </div>

        <div>
          <label className={labelClass}>{t('booking.email')}</label>
          <div className="relative">
            <FieldIcon icon={Mail} />
            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder={t('booking.email_placeholder')} data-testid="booking-email" className={`${errors.email ? inputErrorClass : inputClass} pl-10`} />
          </div>
          {errors.email && <p className={errorClass}><AlertCircle size={12} />{errors.email}</p>}
        </div>

        <div className="md:col-span-2">
          <label className={labelClass}>{t('booking.notes')}</label>
          <div className="relative">
            <div className="absolute left-3.5 top-3.5 text-[#555] pointer-events-none"><MessageSquare size={15} /></div>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder={t('booking.notes_placeholder')} data-testid="booking-notes" className={`${inputClass} pl-10 resize-none`} />
          </div>
        </div>
      </div>

      {apiError && (
        <div data-testid="booking-error-msg" className="mt-4 flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{apiError}</p>
        </div>
      )}

      <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="mt-6 w-full bg-[#EAB308] hover:bg-[#CA8A04] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-4 rounded-xl text-base transition-colors duration-300 flex items-center justify-center gap-2">
        {loading ? (<><Loader size={18} className="animate-spin" />{t('booking.submitting')}</>) : t('booking.submit')}
      </button>

      <p className="text-center text-[#555] text-xs mt-3">{t('booking.footer_note')}</p>
    </form>
  );
};

export default BookingForm;
